<template>
  <div class="fade-in">
    <div class="modal-backdrop" style="z-index: 1" />
      <div class="modal-card">
        <img src="~@/assets/icons/exit.svg" alt="close" class="exit-btn" @click="exit" />
          <transition name="fade">
            <div v-if="mode === 'Error'" class="view">
              <p class="text--l">Whoops,we encountered an unknown error trying to start self-service!</p>
            </div>
          </transition>

          <transition name="fade" mode="out-in">
            <LoadingIndicator v-if="loading" class="fill-height" />
            <template v-else>
              <NewProjectFlowLandingText
                v-if="ssvRunner && mode === 'Landing'"
                class="view"
                key="landing-view"
                :first-name="app_user.firstName"
                :self-service-alias="selfServiceAlias"
                :runnerState="ssvRunner.getState()"
                @start="startRunner"
                @exit="exit"
              />

              <SelfServiceVettingRunner
                v-if="ssvRunner && mode === 'Runner'"
                :runner="ssvRunner"
                key="runner-view"
                @complete="mode = 'Outcome'"
                @setProjectProperty="setProjectProperty"
              />

              <NewProjectFlowOutcome
                v-if="mode === 'Outcome'"
                key="outcome-view"
                :runner="ssvRunner"
                :custom-text="customText"
                @submitApproved="initSelfServiceProject"
                @submitPipeline="initPipelineProject"
                @submitRejected="rejectSelfService"
              />
            </template>
          </transition>
      </div>
  </div>
</template>

<script>
import moment from "moment";
import LoadingIndicator from "@/components/LoadingIndicator";
import {SSVConfig, SSVRunner} from "@/utils/selfServeVetting.ts";
import NewProjectFlowLandingText from "@/components/SelfService/NewProjectFlowLandingText.vue";
import SelfServiceVettingRunner from "@/components/SelfService/SelfServiceVettingRunner.vue";
import NewProjectFlowOutcome from "@/components/SelfService/NewProjectFlowOutcome.vue";
export default {
  name: "NewProjectFlow",
  components: {NewProjectFlowOutcome, SelfServiceVettingRunner, NewProjectFlowLandingText, LoadingIndicator},
  data: () => ({
    loading: false,
    ssvRunner: null,
    project: {},
    mode: null
  }),
  created() {
    const ssvConfig = new SSVConfig(this.selfServiceVettingConfig);
    try {
      this.ssvRunner = new SSVRunner(ssvConfig);
      this.mode = 'Landing';
      return;
    } catch (e) {
      console.error(e);
    }

    this.mode = 'Error';
  },
  methods: {
    selectDate(date){
      this.completeStep(moment(date).format('DD/MM/yy'))
    },
    exit(){
      if (this.ssvRunner && this.ssvRunner.getState() === 'Running') {
        this.abandonSelfServiceVetting();
      }
      this.$emit('exit');
    },
    startRunner(){
      this.mode = 'Runner';
    },
    getProjectDraft(){
      const {name, categoryId, type} = this.ssvRunner.getProjectData();
      const isSelfService = type === 'Self-Service';
      return {
        categoryId,
        name: name || 'New Project',
        startDate: new Date(),
        status: isSelfService ? 'Active' : 'Pipeline',
        type: isSelfService ? 'Self-Service' : 'Other',
        currencyId: this.$store.getters.org.currencyId,
        projectMembers: [{type: isSelfService ? 'Owner': 'Team', userId: this.app_user.id}],
        ...this.project
      }
    },
    setProjectProperty(event) {
      const {key, value} = event;
      switch (key) {
        case 'name': {
          this.project.name = value;
          break;
        }
      }
    },
    async initSelfServiceProject(){
      try{
        this.loading = true;
        const payload = {
          project: this.getProjectDraft(),
          responses: this.ssvRunner.getResponses(),
          session: this.ssvRunner.getSession()
        }

        const {createdProject} = await this.$store.dispatch('createSelfServiceProject', payload)

        // TODO - is it better to getProjectDetail than setting the project directly? getProjectDetail processes the project as well
        // (might not be because we're going to open the project after this and that runs getProjectDetail)
        // await this.$store.dispatch('getProjectDetail', this.createdProject.id);
        await this.$store.dispatch('setProject', createdProject)

        this.$emit('newSelfServiceProject', createdProject.shortId)
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false;
      }
    },
    async initPipelineProject() {
      try{
        this.loading = true;
        const payload = {
          project: this.getProjectDraft(),
          responses: this.ssvRunner.getResponses(),
          outcome: this.ssvRunner.getOutcome()
        }

        const {createdProject} = await this.$store.dispatch('createSelfServiceProject', payload)

        // TODO - is it better to getProjectDetail than setting the project directly? getProjectDetail processes the project as well
        // (might not be because we're going to open the project after this and that runs getProjectDetail)
        // await this.$store.dispatch('getProjectDetail', this.createdProject.id);
        await this.$store.dispatch('setProject', createdProject)

        this.pipelineProject = createdProject;
        this.$emit('newPipelineProject', createdProject)
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false;
      }
    },
    async rejectSelfService() {
      try{
        const payload = {
          session: this.ssvRunner.getSession(),
          responses: this.ssvRunner.getResponses()
        }

        this.$store.dispatch('createSelfServiceVettingSession', payload)
      } catch (e) {
        console.error(e)
      } finally {
        this.exit();
      }
    },
    async abandonSelfServiceVetting() {
      try{
        // We should only abandon running sessions
        if (this.ssvRunner.getState() !== 'Running') return;

        const payload = {
          session: {...this.ssvRunner.getSession(), outcome: 'Abandoned', endTime: new Date()},
          responses: this.ssvRunner.getResponses()
        }

        this.$store.dispatch('createSelfServiceVettingSession', payload)
      } catch (e) {
        console.error(e)
      }
    }
  },
  computed: {
    selfServiceVettingConfig(){
      return this.$store.getters.selfServiceVettingConfig;
    },
    selfServiceAlias(){
      return this.$store.getters.selfServiceAlias
    },
    app_user() {
      return this.$store.getters.app_user;
    },
    org(){
      return this.$store.getters.org;
    },
    customText() {
      return this.selfServiceVettingConfig.customText || {};
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/styles/SelfService/spacing.scss";
@import "~@/styles/SelfService/effects.scss";

.modal-card{
  position: absolute;
  top: 15%;
  left: 15%;
  height: 70%;
  width: 70%;
  display: flex;
  flex-direction: column;
  background-color: white;
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.25), 0 20px 25px rgba(0, 0, 0, 0.25);
  border-radius: 10px;
  padding: $spacing--xl $spacing--xxl 0;
  z-index: 2;
  overflow-x: hidden;

  .view-container {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    .view {
      height: 100%;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }

  .exit-btn{
    position: absolute;
    top: 16px;
    right: 16px;
    cursor: pointer;
  }

  .typeform {
    display: flex;
    align-content: center;
    //align-items: center;
    padding: $spacing--l;

    .step {
      display: flex;
      align-items: center;
    }
  }
}

.view-slide-enter-active {
  transition: transform 0.3s ease-out;
}
.view-slide-leave-active {
  transition: transform 0.3s ease-in;
}
.view-slide-enter {
  transform: translateX(100%);
}
.view-slide-enter-to {
  transform: translateX(0%);
}
.view-slide-leave {
  transform: translateX(0%);
}
.view-slide-leave-to {
  transform: translateX(-100%);
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.3s;
  transition-property: opacity;
  transition-timing-function: ease-out;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.fade-in {
  opacity: 1;
  animation-name: fadeInOpacity;
  //animation-iteration-count: 1;
  animation-timing-function: ease-in;
  animation-duration: 0.2s;
}

@keyframes fadeInOpacity {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
